/** Per-map personal bests in localStorage. Versioned: bump VERSION and extend
 * `migrate` on schema changes — records must survive updates (ROADMAP P7). */

export interface RunEntry {
  /** Run time in simulation ticks (100 Hz). */
  ticks: number;
  /** ISO date of the run. */
  date: string;
}

export interface MapRecord {
  /** Run time in simulation ticks (100 Hz). */
  pbTicks: number;
  /** Checkpoint split ticks, in order. */
  splits: number[];
  /** ISO date of the run. */
  date: string;
  /** Top-10 valid runs, ascending by ticks. */
  runs?: RunEntry[];
}

export interface Records {
  version: 1;
  maps: Record<string, MapRecord>;
}

const STORAGE_KEY = 'fall-line.records';

export function migrateRecords(raw: unknown): Records {
  const out: Records = { version: 1, maps: {} };
  if (typeof raw !== 'object' || raw === null) {
    return out;
  }
  const maps = (raw as Record<string, unknown>).maps;
  if (typeof maps !== 'object' || maps === null) {
    return out;
  }
  for (const [mapId, record] of Object.entries(maps as Record<string, unknown>)) {
    if (typeof record !== 'object' || record === null) {
      continue;
    }
    const r = record as Record<string, unknown>;
    if (typeof r.pbTicks === 'number' && r.pbTicks > 0 && Number.isFinite(r.pbTicks)) {
      const rawRuns = Array.isArray(r.runs) ? r.runs : [];
      const validRuns: RunEntry[] = rawRuns
        .filter((e): e is Record<string, unknown> => {
          if (typeof e !== 'object' || e === null) return false;
          const rec = e as Record<string, unknown>;
          return (
            typeof rec.ticks === 'number' &&
            Number.isFinite(rec.ticks) &&
            (rec.ticks as number) > 0 &&
            typeof rec.date === 'string'
          );
        })
        .map((e) => ({
          ticks: e.ticks as number,
          date: e.date as string,
        }))
        .sort((a, b) => a.ticks - b.ticks)
        .slice(0, 10);
      out.maps[mapId] = {
        pbTicks: r.pbTicks,
        splits: Array.isArray(r.splits) ? r.splits.filter((s) => typeof s === 'number') : [],
        date: typeof r.date === 'string' ? r.date : '',
        runs: validRuns,
      };
    }
  }
  return out;
}

export function loadRecords(): Records {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return migrateRecords(raw ? JSON.parse(raw) : null);
  } catch {
    return { version: 1, maps: {} };
  }
}

/** Records a finished valid run; returns true if it's a new PB. */
export function submitRun(
  records: Records,
  mapId: string,
  ticks: number,
  splits: number[],
): boolean {
  const existing = records.maps[mapId];
  const now = new Date().toISOString();

  // Build updated runs list: insert new entry, sort ascending, cap at 10.
  const prevRuns: RunEntry[] = existing?.runs ?? [];
  const updatedRuns: RunEntry[] = [...prevRuns, { ticks, date: now }]
    .sort((a, b) => a.ticks - b.ticks)
    .slice(0, 10);

  const isPb = existing === undefined || ticks < existing.pbTicks;

  if (isPb) {
    records.maps[mapId] = {
      pbTicks: ticks,
      splits,
      date: now,
      runs: updatedRuns,
    };
  } else {
    records.maps[mapId] = {
      ...existing,
      runs: updatedRuns,
    };
  }

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(records));
  } catch {
    // Storage unavailable — the record lives for this session only.
  }
  return isPb;
}
